import { useEffect, useState, useMemo, useRef } from 'react';

import GeoComplySingleton from '~/utils/geocomply';

function getLocation() {
  const tokenData = GeoComplySingleton.getLocationTokenData();

  return {
    locationState: tokenData?.state,
    locationStatus: tokenData?.status,
    locationCountry: tokenData?.country,
    locationErrors: tokenData?.errors,
    locationError: tokenData?.errors?.[0],
  };
}

function useLocation() {
  const [location, setLocation] = useState<ReturnType<typeof getLocation>>(getLocation());
  const previousLocationRef = useRef(location);

  useEffect(() => {
    const unsubscribe = GeoComplySingleton.subscribeToLocationToken(() => {
      setLocation(getLocation());
    });
    return () => unsubscribe();
  }, []);

  // Memoize the location object to prevent unnecessary re-renders
  // Only return a new object if the values have actually changed
  return useMemo(() => {
    const currentErrors = JSON.stringify(location.locationErrors);
    const previousErrors = JSON.stringify(previousLocationRef.current.locationErrors);

    const hasChanged =
      location.locationState !== previousLocationRef.current.locationState ||
      location.locationStatus !== previousLocationRef.current.locationStatus ||
      location.locationCountry !== previousLocationRef.current.locationCountry ||
      location.locationError !== previousLocationRef.current.locationError ||
      currentErrors !== previousErrors;

    if (hasChanged) {
      previousLocationRef.current = location;
      return location;
    }

    return previousLocationRef.current;
  }, [location]);
}

export { useLocation };
